import { useState, useEffect, useRef } from 'react';
import moment from 'moment-timezone'; // Add this import

const CustomCalendar = ({ selectedDate, onDateSelect, doctorName, doctorImage, onTimeSelect, selectedTime }) => {
  const [currentDate, setCurrentDate] = useState(selectedDate || new Date());
  const [selectedDay, setSelectedDay] = useState(selectedDate);
  const [selectedTimezone, setSelectedTimezone] = useState('PST');
  const timeSlotRef = useRef(null);

  // Get user's timezone or default to PST
  const [userTimezone, setUserTimezone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
    } catch (e) {
      return 'America/Los_Angeles';
    }
  });

  useEffect(() => {
    if (selectedTime && timeSlotRef.current) {
      const selectedButton = timeSlotRef.current.querySelector(`[data-time="${selectedTime}"]`);
      if (selectedButton) {
        selectedButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedTime]);

  const daysInMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  ).getDay();

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleDateClick = (day) => {
    const selected = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDay(selected);
    onDateSelect(selected);
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear();
  };

  const isSelected = (day) => {
    return selectedDay &&
      day === selectedDay.getDate() &&
      currentDate.getMonth() === selectedDay.getMonth() &&
      currentDate.getFullYear() === selectedDay.getFullYear();
  };

  // Get doctor's initial if no image
  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : '?';
  };

  // Generate time slots for selected timezone
  const generateTimeSlots = () => {
    const slots = [];
    const interval = 15; // 15 minutes interval
    const now = moment().tz('America/Los_Angeles'); // Base time in PST
    const currentTime = now.clone();
    
    // Start from 6:00 AM PST
    const startTime = now.clone().set({ hour: 6, minute: 0 });
    const endTime = now.clone().set({ hour: 20, minute: 0 }); // 8:00 PM PST

    while (startTime.isBefore(endTime)) {
      const timeInUserTZ = startTime.clone().tz(userTimezone);
      const timeString = timeInUserTZ.format('hh:mm A');
      
      slots.push({
        displayTime: timeString,
        pstTime: startTime.format('HH:mm')
      });

      startTime.add(interval, 'minutes');
    }

    // Set default selected time to next available slot if viewing today
    if (!selectedTime && selectedDay?.getDate() === now.date()) {
      const nextAvailable = slots.find(slot => {
        const [hours, minutes] = slot.pstTime.split(':');
        return (
          parseInt(hours) > now.hours() || 
          (parseInt(hours) === now.hours() && parseInt(minutes) > now.minutes())
        );
      });
      
      if (nextAvailable) {
        onTimeSelect(nextAvailable.displayTime);
      }
    }

    return slots;
  };

  // Modified timezone options with moment-timezone names
  const timezones = [
    { value: 'America/Los_Angeles', label: 'Pacific Time (PST)' },
    { value: 'America/Denver', label: 'Mountain Time (MST)' },
    { value: 'America/Chicago', label: 'Central Time (CST)' },
    { value: 'America/New_York', label: 'Eastern Time (EST)' },
  ];

  const handleTimezoneChange = (e) => {
    setUserTimezone(e.target.value);
    setSelectedTime(''); // Reset selected time when timezone changes
  };

  return (
    <div className="w-full bg-white rounded-lg shadow-lg">
      {/* Profile Section */}
      <div className="p-6 text-center border-b border-gray-200">
        {doctorImage ? (
          <img 
            src={doctorImage} 
            alt={doctorName}
            className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-gray-100"
          />
        ) : (
          <div className="w-20 h-20 rounded-full mx-auto mb-4 bg-blue-500 flex items-center justify-center">
            <span className="text-3xl font-semibold text-white">
              {getInitial(doctorName)}
            </span>
          </div>
        )}
        <p className="text-gray-600 mb-4">Pick a date and time that works best for you</p>
      </div>

      {/* Calendar Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-800">
            {months[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid with enhanced styling */}
      <div className="p-6">
        <div className="grid grid-cols-7 gap-2 mb-4">
          {days.map(day => (
            <div key={day} className="text-center text-sm font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {[...Array(firstDayOfMonth)].map((_, index) => (
            <div key={`empty-${index}`} className="h-12" />
          ))}
          {[...Array(daysInMonth)].map((_, index) => {
            const day = index + 1;
            return (
              <button
                key={day}
                onClick={() => handleDateClick(day)}
                disabled={new Date(currentDate.getFullYear(), currentDate.getMonth(), day) < new Date()}
                className={`
                  h-12 rounded-lg text-sm font-medium relative
                  ${isToday(day) ? 'border-2 border-blue-500' : ''}
                  ${isSelected(day) 
                    ? 'bg-blue-500 text-white hover:bg-blue-600' 
                    : 'hover:bg-blue-50 hover:text-blue-600'}
                  ${new Date(currentDate.getFullYear(), currentDate.getMonth(), day) < new Date() 
                    ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                    : 'text-gray-700'
                  }
                  transition-all duration-200 ease-in-out
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                `}
              >
                {day}
                {isSelected(day) && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time Slots Section */}
      {selectedDay && (
        <div className="border-t border-gray-200 mt-4">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Available Times ({moment.tz(userTimezone).zoneName})
              </h3>
              <select
                value={userTimezone}
                onChange={handleTimezoneChange}
                className="block w-48 px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {timezones.map(tz => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
            <div 
              ref={timeSlotRef}
              className="max-h-[250px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pr-2"
            >
              <div className="grid grid-cols-1 gap-2">
                {generateTimeSlots().map(({ displayTime, pstTime }) => (
                  <button
                    key={pstTime}
                    data-time={displayTime}
                    onClick={() => onTimeSelect(displayTime)}
                    className={`
                      w-full py-3 px-4 text-sm rounded-md transition-colors
                      flex items-center justify-center font-medium
                      ${selectedTime === displayTime 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}
                    `}
                  >
                    {displayTime}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomCalendar;
